<template>
  <v-dialog max-width="500px" persistent v-model="dialog">
    <v-card class="reset-password-card" v-if="customer != null">
      <!-- Header -->
      <div class="reset-header">
        <div class="header-icon">
          <v-icon color="#fddf00" size="32">lock_reset</v-icon>
        </div>
        <div class="header-content">
          <h2 class="header-title">Reset Password</h2>
          <p class="header-subtitle">Create a new secure password</p>
        </div>
        <v-btn icon @click="$emit('setDialog', false)" class="close-btn">
          <v-icon>close</v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <v-card-text class="reset-content">
        <!-- Customer Info Card -->
        <div class="customer-info-card">
          <div class="customer-avatar">
            <v-icon color="#fddf00" size="24">person</v-icon>
          </div>
          <div class="customer-details">
            <h3 class="customer-name">
              {{ customer.user_first_name }} {{ customer.user_last_name }}
            </h3>
            <p class="customer-email">{{ customer.user_email }}</p>
            <v-chip
              v-if="customer.user_vip"
              color="#fddf00"
              size="x-small"
              class="vip-badge"
            >
              <v-icon left size="12">star</v-icon>
              VIP
            </v-chip>
          </div>
        </div>

        <!-- Password Form -->
        <v-form ref="passwordForm" v-model="valid" class="password-form">
          <!-- New Password Field -->
          <div class="password-field-container">
            <v-text-field
              v-model="password1"
              :type="show1 ? 'text' : 'password'"
              label="New Password"
              outlined
              color="#fddf00"
              :rules="[
                passwordValid.required,
                passwordValid.min,
                passwordValid.max,
              ]"
              class="modern-input"
              @input="validatePasswords"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00">lock</v-icon>
              </template>
              <template v-slot:append>
                <v-btn icon @click="show1 = !show1" class="visibility-btn">
                  <v-icon>{{ show1 ? "visibility_off" : "visibility" }}</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <!-- Password Requirements -->
            <div class="password-requirements">
              <div class="requirement-title">Password must contain:</div>
              <div class="requirements-list">
                <div
                  class="requirement-item"
                  :class="{ 'requirement-met': requirements.minLength }"
                >
                  <v-icon
                    size="16"
                    :color="requirements.minLength ? 'success' : 'grey'"
                  >
                    {{
                      requirements.minLength
                        ? "check_circle"
                        : "radio_button_unchecked"
                    }}
                  </v-icon>
                  <span>At least 6 characters</span>
                </div>
                <div
                  class="requirement-item"
                  :class="{ 'requirement-met': requirements.hasUpper }"
                >
                  <v-icon
                    size="16"
                    :color="requirements.hasUpper ? 'success' : 'grey'"
                  >
                    {{
                      requirements.hasUpper
                        ? "check_circle"
                        : "radio_button_unchecked"
                    }}
                  </v-icon>
                  <span>One uppercase letter</span>
                </div>
                <div
                  class="requirement-item"
                  :class="{ 'requirement-met': requirements.hasLower }"
                >
                  <v-icon
                    size="16"
                    :color="requirements.hasLower ? 'success' : 'grey'"
                  >
                    {{
                      requirements.hasLower
                        ? "check_circle"
                        : "radio_button_unchecked"
                    }}
                  </v-icon>
                  <span>One lowercase letter</span>
                </div>
                <div
                  class="requirement-item"
                  :class="{
                    'requirement-met': requirements.hasNumberOrSpecial,
                  }"
                >
                  <v-icon
                    size="16"
                    :color="
                      requirements.hasNumberOrSpecial ? 'success' : 'grey'
                    "
                  >
                    {{
                      requirements.hasNumberOrSpecial
                        ? "check_circle"
                        : "radio_button_unchecked"
                    }}
                  </v-icon>
                  <span>One number or special character</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Confirm Password Field -->
          <div class="password-field-container">
            <v-text-field
              v-model="password2"
              :type="show2 ? 'text' : 'password'"
              label="Confirm New Password"
              outlined
              color="#fddf00"
              :rules="[passwordValid.required, passwordValid.match]"
              class="modern-input"
              @input="validatePasswords"
            >
              <template v-slot:prepend-inner>
                <v-icon color="#fddf00">lock_outline</v-icon>
              </template>
              <template v-slot:append>
                <v-btn icon @click="show2 = !show2" class="visibility-btn">
                  <v-icon>{{ show2 ? "visibility_off" : "visibility" }}</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <!-- Password Match Indicator -->
            <div v-if="password2.length > 0" class="password-match-indicator">
              <div
                class="match-status"
                :class="{
                  'match-success': passwordsMatch && password2.length > 0,
                  'match-error': !passwordsMatch && password2.length > 0,
                }"
              >
                <v-icon size="16" :color="passwordsMatch ? 'success' : 'error'">
                  {{ passwordsMatch ? "check_circle" : "cancel" }}
                </v-icon>
                <span>
                  {{
                    passwordsMatch
                      ? "Passwords match"
                      : "Passwords do not match"
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Password Strength Indicator -->
          <div class="strength-indicator">
            <div class="strength-header">
              <span class="strength-label">Password Strength</span>
              <span class="strength-text">{{ passwordStrength.text }}</span>
            </div>
            <div class="strength-bar-container">
              <div
                class="strength-bar"
                :class="`strength-${passwordStrength.level}`"
                :style="{ width: `${passwordStrength.percentage}%` }"
              ></div>
            </div>
          </div>
        </v-form>

        <!-- Security Notice -->
        <div class="security-notice">
          <v-icon color="info" size="20">info</v-icon>
          <div class="notice-content">
            <p class="notice-title">Security Notice</p>
            <p class="notice-text">
              The customer will be notified of this password change via email.
              They should log in with the new password immediately.
            </p>
          </div>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="reset-actions">
        <v-btn text color="grey" @click="cancel()" class="cancel-btn">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="#fddf00"
          :disabled="!canResetPassword"
          :loading="isResetting"
          @click="resetPassword()"
          class="reset-btn"
        >
          <v-icon left>lock_reset</v-icon>
          Reset Password
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: ["dialog", "customer"],

  data() {
    return {
      password1: "",
      password2: "",
      show1: false,
      show2: false,
      valid: false,
      isResetting: false,
      passwordValid: {
        required: (value) => !!value || "Password is required",
        min: (v) => (v && v.length >= 6) || "At least 6 characters required",
        max: (v) => (v && v.length <= 24) || "Maximum 24 characters allowed",
        match: () => this.passwordsMatch || "Passwords do not match",
      },
    };
  },

  computed: {
    passwordsMatch() {
      return this.password1 === this.password2 && this.password1.length > 0;
    },

    requirements() {
      return {
        minLength: this.password1.length >= 6,
        hasUpper: /[A-Z]/.test(this.password1),
        hasLower: /[a-z]/.test(this.password1),
        hasNumberOrSpecial:
          /[0-9]/.test(this.password1) || /[^A-Za-z0-9]/.test(this.password1),
      };
    },

    passwordStrength() {
      if (!this.password1) {
        return { level: "none", percentage: 0, text: "Enter a password" };
      }

      const requirements = this.requirements;
      const metRequirements =
        Object.values(requirements).filter(Boolean).length;
      const percentage = (metRequirements / 4) * 100;

      if (percentage <= 25) {
        return { level: "weak", percentage, text: "Weak" };
      } else if (percentage <= 50) {
        return { level: "fair", percentage, text: "Fair" };
      } else if (percentage <= 75) {
        return { level: "good", percentage, text: "Good" };
      } else {
        return { level: "strong", percentage, text: "Strong" };
      }
    },

    canResetPassword() {
      return (
        this.valid &&
        this.passwordsMatch &&
        this.password1.length >= 6 &&
        Object.values(this.requirements).every(Boolean)
      );
    },
  },

  methods: {
    validatePasswords() {
      this.$nextTick(() => {
        if (this.$refs.passwordForm) {
          this.$refs.passwordForm.validate();
        }
      });
    },

    cancel() {
      this.clearForm();
      this.$emit("setDialog", false);
    },

    async resetPassword() {
      if (!this.canResetPassword) return;

      this.isResetting = true;

      try {
        const sendData = {
          user_id: this.customer.user_id,
          new_password: this.password2,
        };

        const result = await this.$store.dispatch(
          "fetchUserBack/changePasswordUser",
          sendData
        );

        if (result !== "error") {
          this.$emit("setDialog", false);
          this.clearForm();
          this.$emit("password-reset-success", {
            customer: this.customer,
            message: "Password reset successfully",
          });
        } else {
          this.$emit("password-reset-error", {
            customer: this.customer,
            message: "Failed to reset password",
          });
        }
      } catch (error) {
        console.error("Password reset error:", error);
        this.$emit("password-reset-error", {
          customer: this.customer,
          message: "An error occurred while resetting password",
        });
      } finally {
        this.isResetting = false;
      }
    },

    clearForm() {
      this.password1 = "";
      this.password2 = "";
      this.show1 = false;
      this.show2 = false;
      if (this.$refs.passwordForm) {
        this.$refs.passwordForm.resetValidation();
      }
    },
  },

  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.clearForm();
      }
    },
  },
};
</script>

<style scoped>
.reset-password-card {
  border-radius: 16px;
  overflow: hidden;
}

.reset-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #fddf00 0%, #f39c12 100%);
  gap: 1rem;
}

.header-icon {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: black;
  margin: 0;
}

.header-subtitle {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.8);
  margin: 0.25rem 0 0 0;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1) !important;
  color: black !important;
}

.reset-content {
  padding: 2rem;
}

.customer-info-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(253, 223, 0, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(253, 223, 0, 0.2);
  margin-bottom: 2rem;
}

.customer-avatar {
  background: rgba(253, 223, 0, 0.2);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-details {
  flex: 1;
}

.customer-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 0.25rem 0;
}

.customer-email {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.vip-badge {
  color: black !important;
  font-weight: 600;
}

.password-form {
  margin-bottom: 1.5rem;
}

.password-field-container {
  margin-bottom: 1.5rem;
}

.modern-input {
  margin-bottom: 0.5rem;
}

.visibility-btn {
  color: #6b7280 !important;
}

.password-requirements {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.5rem;
}

.requirement-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0.75rem;
}

.requirements-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
  transition: color 0.2s ease;
}

.requirement-item.requirement-met {
  color: #059669;
}

.password-match-indicator {
  margin-top: 0.5rem;
}

.match-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 0.5rem;
  border-radius: 6px;
}

.match-success {
  background: rgba(5, 150, 105, 0.1);
  color: #059669;
}

.match-error {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.strength-indicator {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.strength-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.strength-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4b5563;
}

.strength-text {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.strength-bar-container {
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.strength-bar {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 3px;
}

.strength-weak {
  background: #dc2626;
}

.strength-fair {
  background: #f59e0b;
}

.strength-good {
  background: #3b82f6;
}

.strength-strong {
  background: #059669;
}

.security-notice {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
}

.notice-content {
  flex: 1;
}

.notice-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 0.25rem 0;
}

.notice-text {
  font-size: 0.8rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.4;
}

.reset-actions {
  padding: 1rem 2rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn {
  font-weight: 600;
  text-transform: none;
  color: white;
}

.reset-btn {
  font-weight: 600;
  text-transform: none;
  color: black !important;
  border-radius: 8px;
  min-width: 140px;
}

.reset-btn:disabled {
  opacity: 0.6 !important;
}

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .reset-header {
    padding: 1rem 1.5rem;
  }

  .reset-content {
    padding: 1.5rem;
  }

  .requirements-list {
    grid-template-columns: 1fr;
  }

  .reset-actions {
    padding: 1rem 1.5rem;
    flex-direction: column;
    gap: 1rem;
  }

  .reset-btn {
    width: 100%;
  }
}
</style>
